import { SupportedFileType } from '../../components/filePreview/FilePreview';
import { TableItem } from '../../components/table/DataTable.types';
import { EntityType } from '../../types/Files';

export interface ObjectFromEnum {
  value: string;
  name: string;
}

/**
 * Converts enum to array of objects, to use in select components
 * @param e - enum to convert
 * @param translateObj - enum with translations
 * @param t - translate hook
 * @returns array
 */
export const convertEnumToArray = (
  e: object,
  translateObj?: any,
  t?: (query: string) => string,
) => {
  const arr: ObjectFromEnum[] = Object.values(e).map((o: any) => {
    return { value: o, name: translateObj && t ? t(translateObj[o]) : o };
  });
  return arr;
};

/**
 * Converts array to array of objects, to use in select components
 * @param e
 * @param translateEnum
 * @param entity
 */
export const convertArrayToObjArray = (
  e: any[],
  translateEnum: any,
  t: (query: string, entity?: EntityType) => string,
  entity?: EntityType,
) => {
  const arr: any[] = e.map((o: any) => {
    return { value: o, name: t(translateEnum[o], entity) };
  });
  return arr;
};

/**
 *  filters data object from values where value is empty strings
 */
export const filterObjectFromEmptyString = (data: any) => {
  return Object.entries(data).reduce((tmp: any, [key, value]) => {
    if (value !== '') {
      tmp[key] = value;
    }
    return tmp;
  }, {});
};

/**
 * Search by query in all columns of table
 * @param query
 * @param data
 * @param includeColumns - keys of object to search in
 * @returns filtered data
 * TODO - dates, translated values?
 */
export const searchByQuery = (query: string, data: TableItem[], includeColumns?: string[]) => {
  if (query === '' || !query) return data;
  let _filteredData: TableItem[] = [];
  data?.map((item: any) => {
    let putItemInArray = false;
    // for every key in object
    Object.keys(item).map((key: string) => {
      // if includeColumns not empty, search only in these columns
      if ((includeColumns && !includeColumns.includes(key)) || putItemInArray) return;
      // check if array
      if (item[key] && typeof item[key] === 'object') {
        if (item[key].length && item[key].length > 0) {
          // for each child
          item[key].forEach((i: any) => {
            // for every child´s key
            Object.keys(i).map((k: string) => {
              // contains query?
              if (containsQuery(i[k], query)) {
                putItemInArray = true;
              }
            });
          });
        } else {
          Object.keys(item[key]).map((k: string) => {
            // contains query?
            if (containsQuery(item[key][k], query)) {
              putItemInArray = true;
            }
          });
        }
        if (putItemInArray) return (_filteredData = [..._filteredData, item]);
      } else {
        // not array - check if contains query
        if (containsQuery(item[key], query)) {
          putItemInArray = true;
          return (_filteredData = [..._filteredData, item]);
        }
      }
    });
  });
  return _filteredData;
};

/**
 * Check if value contains query
 */
const containsQuery = (val: any, query: string) => {
  return val && val.toString().toLowerCase().includes(query.toLowerCase());
};

/**
 * Format price - separates thousands and sets min fraction digits
 * @param price
 */
export const formatPrice = (price: number, minDigits?: number, maxDigits?: number) => {
  // min digits to use in decimal part
  const minDigitsToUse = minDigits ? minDigits : 2;
  const formattedPrice = price.toString();
  const [integerPart, decimalPart] = formattedPrice.includes(',')
    ? formattedPrice.split(',')
    : formattedPrice.split('.');
  // separate thousands by space
  const integerPartWithSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  let formattedResult = integerPartWithSeparator;
  if (decimalPart) {
    if (decimalPart.length < minDigitsToUse) {
      // decimal part lesser than minDigitsToUse - add zeros
      const addedDigits = minDigitsToUse - decimalPart.length;
      const paddedDecimalPart = decimalPart + '0'.repeat(addedDigits);
      const slicedDecimalPart = paddedDecimalPart.slice(0, maxDigits || 2);
      formattedResult += `,${slicedDecimalPart}`;
    } else {
      // decimal part greater than minDigitsToUse - slice
      const slicedDecimalPart = decimalPart.slice(0, maxDigits || 2);
      formattedResult += `,${slicedDecimalPart}`;
    }
  } else {
    // no decimal part - add zeros to match minimum decimal digits
    formattedResult += `,${'0'.repeat(minDigitsToUse)}`;
  }
  return formattedResult;
};

/**
 * Check if string contains value from enum and returns it
 * @param text
 */
export const getEnumValueContainedInString = (text: string, e: any) => {
  for (const key in e) {
    if (text.includes(e[key])) {
      return e[key];
    }
  }
  // does not contain
  return null;
};

/**
 * Return true/false if content type of file is supported for preview
 * @param contentType
 */
export const supportedFileType = (contentType: string) => {
  return Object.values(SupportedFileType).includes(contentType as SupportedFileType);
};

/**
 * Checks if two arrays are equal
 * @param arr1
 * @param arr2
 */
export const areArraysEqual = (arr1: any[], arr2: any[]) => {
  const jsonArr1 = JSON.stringify(arr1);
  const jsonArr2 = JSON.stringify(arr2);
  // Compare the JSON strings
  return jsonArr1 === jsonArr2;
};
